<template>
  <FormItem 
    :id="id || name"
    :label="label"
    :label-width="labelWidth"
    :required="isRequired"
    :layout="layout"
    :error-message="errorMessage"
    :help-text="helpText"
    :no-margin="noMargin || isNested"
  >
    <div class="date-input-wrapper" :class="{ 'is-error': errorMessage }">
      <div style="display:none">
        <input type="text" autocomplete="username" value=" " />
        <input type="password" autocomplete="current-password" value=" " />
      </div>
      <el-date-picker
        ref="datePicker"
        v-model="inputValue"
        style="width: 100%; height: 36px;"
        :style="$attrs.style"
        :clearable="clearable"
        v-bind="$attrs"
        @change="handleChange" 
        @visible-change="handleVisibleChange"
        @calendar-change="handleCalendarChange"
        @focus="handleFocus"
        @blur="handleBlur"



      />
      <div class="suffix-container">
        <div v-if="clearable && inputValue" class="clear-btn" @click="clearInput">
          <SvgIcon iconName="icon-icon_universe_close_fill"></SvgIcon>
        </div>
        <div class="date-icon-wrapper" @click="openDatePicker">
          <SvgIcon iconName="icon-icon_universe_calendar" class="date-icon"></SvgIcon>
        </div>
      </div>
    </div>
  </FormItem>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { ElDatePicker } from 'element-plus';
import SvgIcon from '../SvgIcon.vue';
import FormItem from './FormItem.vue';

const props = defineProps({
  id: String,
  name: String,
  modelValue: {
    type: [String, Number, Date, Array],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  isRequired: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  labelWidth: {
    type: String,
    default: '120px'
  },
  layout: {
    type: String,
    default: 'vertical'
  },
  noMargin: {
    type: Boolean,
    default: false
  },
  isNested: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'change', 'clear', 'blur', 'focus', 'visible-change', 'calendar-change']);

const datePicker = ref(null);
const inputValue = ref(props.modelValue);

watch(() => props.modelValue, (val) => {
  inputValue.value = val;
}, { immediate: true });

watch(() => inputValue.value, (val) => {
  emit('update:modelValue', val);
});

const handleChange = (value) => {
  emit('change', value);
};

const emitChange = () => {
  emit('change', inputValue.value);
};

const handleVisibleChange = (visible) => {
  if (visible) {
    const shortcuts = document.querySelectorAll('.el-picker-panel__sidebar .el-picker-panel__shortcut')
    shortcuts.forEach(shortcut => {
      shortcut.removeEventListener('click', emitChange)
      shortcut.addEventListener('click', emitChange)
    })
  }
  emit('visible-change', visible);
};

const handleCalendarChange = (value) => {
  emit('calendar-change', value);
};

const handleFocus = (value) => {
  emit('focus', value);
};

const handleBlur = (value) => {
  emit('blur', value);
};

const openDatePicker = async () => {
  datePicker.value.handleOpen();
};

const clearInput = () => {
  inputValue.value = null;
  emit('update:modelValue', null);
  emit('change');
};
</script>

<style scoped>
.date-input-wrapper {
  position: relative;
  width: 100%;
}
.date-input-wrapper :deep(.el-date-editor .el-range-separator) {
    flex: none !important;
    flex-basis: 35px !important;
}

.date-input-wrapper :deep(.el-input__icon) {
  display: none;
}
.date-input-wrapper :deep(input:focus) {
  outline: none !important;
  border: none !important;
}
.date-input-wrapper :deep(.el-input__wrapper.is-focus) {
  border: 1px solid #0068b0 !important;
}
.date-input-wrapper.is-error :deep(.el-input__wrapper) {
  border: 1px solid #C62741 !important;
}
.date-input-wrapper.is-error :deep(.el-input__wrapper.is-focus) {
  border: 1px solid #0068b0 !important;
}
.date-input-wrapper :deep(.el-date-editor.el-input__wrapper.is-active) {
  border: 1px solid #0068b0 !important;
  box-shadow: inset 0 0 0 1px #3b71ca !important;
  transition: all 0.2s;
}
.date-input-wrapper :deep(.el-input__wrapper) {
  border: 1px solid #D8E8FF !important;
  box-shadow: none !important;
  background-color: #F5FAFF !important;
}


.suffix-container {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 2;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;
  background: none;
  border: none;
  opacity: 0;
  transition: opacity 0.2s;
  color: #8697AA;
  font-size: 14px;
}

.date-input-wrapper:hover .clear-btn {
  opacity: 1;
}

.clear-btn:hover {
  color: #0068B0;
}

.date-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0 16px;
  cursor: pointer;
}

.date-icon {
  color: #0068B0;
  font-size: 16px;
}
</style> 

<style>
.el-date-range-picker__editors-wrap .el-input__inner:focus {
  outline: none !important;
  border: none !important;
}
.el-date-range-picker__editors-wrap .el-inpuit__wrapper.is-focus {
  border-color: #0068b0 !important;
  box-shadow: inset 0 0 0 1px #3b71ca !important;
}
 </style>